import Vue from 'vue';
import { validationMixin } from 'vuelidate';
import { TranslateMap, TranslateMapArray } from '@/@types/member';

interface ShowErrorModel {
  [key: string]: boolean; // prettier-ignore
}

interface Data {
  changeMade: boolean;
  showErrorModel: ShowErrorModel;
}

interface FormValidationMixin {
  constructShowErrorModel: (model: string) => void;
  loopThroughObject: (
    object: any,
    model: ShowErrorModel,
    modelString: string,
  ) => void;
  findSelectedField: (formField: any) => any;
  changeMade: boolean;
  constructErrors: (formField: any, errorArray: string[]) => string[];
  validateField: (
    formField: any,
    errorString: string | string[],
    validationKey: string,
    translateMap?: TranslateMap | TranslateMapArray,
  ) => string;
  updateHasChanged: () => void;
  triggerInputOrChange: (formField: any) => void;
  triggerFocus: (formField: any) => void;
  triggerBlur: (formField: any) => void;
  triggerSelectBlur: (e: any, b: string) => void;
}

const VALIDATION_MIXIN = Vue.extend(validationMixin);
export default VALIDATION_MIXIN.extend({
  data(): Data {
    return {
      changeMade: false,
      showErrorModel: {},
    };
  },
  computed: {
    FormValidationMixin(): FormValidationMixin {
      return {
        constructShowErrorModel: this.constructShowErrorModel,
        loopThroughObject: this.loopThroughObject,
        findSelectedField: this.findSelectedField,
        changeMade: this.changeMade,
        constructErrors: this.constructErrors,
        validateField: this.validateField,
        updateHasChanged: this.updateHasChanged,
        triggerInputOrChange: this.triggerInputOrChange,
        triggerFocus: this.triggerFocus,
        triggerBlur: this.triggerBlur,
        triggerSelectBlur: this.triggerSelectBlur,
      };
    },
  },
  methods: {
    constructShowErrorModel(model: string): void {
      this.loopThroughObject(this[model], this.showErrorModel, model);
    },

    loopThroughObject(
      object: any,
      model: ShowErrorModel,
      modelString: string,
    ): void {
      for (const KEY in object) {
        if (object.hasOwnProperty(KEY)) {
          if (typeof object[KEY] === 'object' && object[KEY] !== null) {
            this.loopThroughObject(object[KEY], model, `${modelString}.${KEY}`);
          } else {
            model[`${modelString}.${KEY}`] = false;
          }
        }
      }
    },

    findSelectedField(formField: any): any {
      // `formField` must be in dot notation
      const FORM_FIELDS = formField.split('.');
      // TODO Form Validation mixin and form validation as a whole needs to be refactored to match current standards
      // @ts-ignore
      let selectedField = this.$v;
      // Build the selected field
      FORM_FIELDS.forEach((val, index) => {
        selectedField = selectedField[FORM_FIELDS[index]];
      });
      return selectedField;
    },

    constructErrors(formField: any, errorArray: string[]): string[] {
      const selectedFormField = this.findSelectedField(formField);
      let errors = [];
      // Return an empty error array if the field is not dirty
      if (!selectedFormField.$dirty || !this.showErrorModel[formField]) {
        return errors;
      }
      // Filter out null, undefined, and duplicate errors
      errors = errorArray.filter((error, index, self) => {
        return error && self.indexOf(error) === index;
      });
      return errors;
    },

    validateField(
      formField: any,
      errorString: string | string[],
      validationKey: string,
      translateMap?: TranslateMap | TranslateMapArray,
    ): string {
      const selectedFormField = this.findSelectedField(formField);
      let errorMessageTranslated = this.$options.filters.t(errorString);
      const VAR_REPLACE_OBJECT = {};

      if (validationKey && translateMap) {
        if (Array.isArray(translateMap)) {
          translateMap.forEach((item) => {
            let paramVal = selectedFormField.$params[validationKey]
              ? selectedFormField.$params[validationKey][item.val]
              : null;
            // Check if `val` is a function, if so execute and set as the value
            if (typeof paramVal === 'function') {
              paramVal = paramVal(this);
            }
            VAR_REPLACE_OBJECT[item.key] = paramVal;
          });
        } else {
          let paramVal = selectedFormField.$params[validationKey]
            ? selectedFormField.$params[validationKey][translateMap.val]
            : null;
          // Check if `val` is a function, if so execute and set as the value
          if (typeof paramVal === 'function') {
            paramVal = paramVal(this);
          }
          VAR_REPLACE_OBJECT[translateMap.key] = paramVal;
        }

        // Run varReplace to find instance of `%VARIABLE%` in the error string
        // and replace with the field's specified VARIABLE value
        errorMessageTranslated = this.$options.filters.varReplace(
          errorMessageTranslated,
          VAR_REPLACE_OBJECT,
        );
      }

      if (!selectedFormField[validationKey]) {
        return errorMessageTranslated;
      }
    },

    updateHasChanged(): void {
      this.changeMade = true;
      this.$emit('updateHasChanged');
    },

    triggerInputOrChange(formField: any): void {
      const selectedFormField = this.findSelectedField(formField);
      selectedFormField.$touch();
      this.updateHasChanged();
    },

    triggerFocus(formField: any): void {
      const selectedFormField = this.findSelectedField(formField);
      selectedFormField.$reset();
      this.showErrorModel[formField] = false;
    },

    triggerBlur(formField: any): void {
      const selectedFormField = this.findSelectedField(formField);
      selectedFormField.$reset();
      this.showErrorModel[formField] = true;
      setTimeout(selectedFormField.$touch, 0);
    },

    triggerSelectBlur(e: any, formField: any): void {
      if (e) {
        const selectedFormField = this.findSelectedField(formField);
        selectedFormField.$reset();
        this.showErrorModel[formField] = true;
        selectedFormField.$touch();
      }
    },
  },
});
